import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useContextMenu } from '../shared/ContextMenu';
import Navbar from '../shared/Navbar';
import StorageBanner from '../shared/StorageBanner';
import '../../css/BoardList.css';

const BoardList = () => {
    const { apiCall, user } = useAuth();
    const [boards, setBoards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [newBoardName, setNewBoardName] = useState('');
    const [newBoardAbbr, setNewBoardAbbr] = useState('');
    const [newBoardDesc, setNewBoardDesc] = useState('');
    const [creating, setCreating] = useState(false);
    const [deletingBoardId, setDeletingBoardId] = useState(null);
    const [storageFull, setStorageFull] = useState(false);
    const navigate = useNavigate();

    // ─── Enhanced Right-Click ────────────────────────────────────────
    // Global options shown when right-clicking non-interactive areas
    const globalMenuOptions = [
        {
            label: "Home",
            icon: <i className="fas fa-home"></i>,
            onClick: () => navigate("/"),
        },
        {
            label: "New Board",
            icon: <i className="fas fa-plus"></i>,
            onClick: () => setShowCreate(true),
        },
        { separator: true },
        {
            label: "Bluebird Documentation",
            icon: <i className="fas fa-book"></i>,
            url: "https://bluebird-documentation.com/",
            onClick: () => window.open("https://bluebird-documentation.com/", "_blank", "noopener,noreferrer"),
        },
    ];

    // Board-card-specific options (dynamic, per-card)
    const getBoardCardContextOptions = (boardData) => {
        if (!boardData) return globalMenuOptions;
        const isOwner = boardData.user_role === 'OWNER';
        const options = [
            {
                label: "Open Board",
                icon: <i className="fas fa-columns"></i>,
                onClick: () => navigate(`/board/${boardData.id}`),
            },
            {
                label: "Open in New Tab",
                icon: <i className="fas fa-external-link-alt"></i>,
                url: `/board/${boardData.id}`,
                onClick: () => window.open(`/board/${boardData.id}`, "_blank", "noopener,noreferrer"),
            },
        ];
        if (isOwner) {
            options.push(
                { separator: true },
                {
                    label: "Delete Board",
                    icon: <i className="fas fa-trash-alt"></i>,
                    onClick: () => {
                        if (window.confirm('Are you sure you want to delete this board? All tasks will be lost.')) {
                            handleDeleteBoard(boardData.id, { stopPropagation: () => {} });
                        }
                    },
                }
            );
        }
        return options;
    };

    const { showContextMenu, handleContextMenu, ContextMenuComponent } = useContextMenu(getBoardCardContextOptions);

    const fetchBoards = useCallback(async () => {
        try {
            const response = await apiCall('/tasks/boards/');
            if (response.ok) {
                const data = await response.json();
                setBoards(data);
            }
        } catch (err) {
            console.error('Error fetching boards:', err);
        }
        setLoading(false);
    }, [apiCall]);

    useEffect(() => {
        fetchBoards();
    }, [fetchBoards]);

    const handleCreateBoard = async (e) => {
        e.preventDefault();
        if (!newBoardName.trim()) return;

        setCreating(true);
        try {
            const response = await apiCall('/tasks/boards/create/', {
                method: 'POST',
                body: JSON.stringify({
                    name: newBoardName.trim(),
                    abbreviation: newBoardAbbr.trim().toUpperCase(),
                    description: newBoardDesc.trim(),
                }),
            });

            if (response.ok) {
                const board = await response.json();
                setBoards(prev => [board, ...prev]);
                setNewBoardName('');
                setNewBoardAbbr('');
                setNewBoardDesc('');
                setShowCreate(false);
                setStorageFull(false);
                navigate(`/board/${board.id}`);
            } else if (response.status === 403) {
                const data = await response.json();
                if (data.storage_full) {
                    setStorageFull(true);
                    setShowCreate(false);
                }
            }
        } catch (err) {
            console.error('Error creating board:', err);
        }
        setCreating(false);
    };

    const handleDeleteBoard = async (boardId, e) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this board? All tasks will be lost.')) return;

        setDeletingBoardId(boardId);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/delete/`, {
                method: 'DELETE',
            });
            if (response.ok) {
                setBoards(prev => prev.filter(b => b.id !== boardId));
            }
        } catch (err) {
            console.error('Error deleting board:', err);
        }
        setDeletingBoardId(null);
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div className="BoardList" onContextMenu={handleContextMenu}>
            <Navbar />
            <div className="BoardList__content">
                <div className="BoardList__header">
                    <div>
                        <h1 className="BoardList__title">My Boards</h1>
                        <p className="BoardList__subtitle">Manage your tasks and projects</p>
                    </div>
                    <button
                        className="BoardList__create-btn"
                        onClick={() => setShowCreate(true)}
                    >
                        + New Board
                    </button>
                </div>

                {storageFull && (
                    <StorageBanner onDismiss={() => setStorageFull(false)} />
                )}

                {showCreate && (
                    <div className="BoardList__create-overlay" onClick={() => setShowCreate(false)}>
                        <form
                            className="BoardList__create-form"
                            onClick={(e) => e.stopPropagation()}
                            onSubmit={handleCreateBoard}
                        >
                            <h3 className="BoardList__create-title">Create New Board</h3>
                            <div className="BoardList__create-field">
                                <label>Board Name</label>
                                <input
                                    type="text"
                                    value={newBoardName}
                                    onChange={(e) => setNewBoardName(e.target.value)}
                                    placeholder="e.g., Sprint 1, Marketing Campaign"
                                    autoFocus
                                    maxLength={255}
                                />
                            </div>
                            <div className="BoardList__create-field">
                                <label>Abbreviation <span className="BoardList__create-hint">(up to 5 letters, used for task IDs)</span></label>
                                <input
                                    type="text"
                                    value={newBoardAbbr}
                                    onChange={(e) => setNewBoardAbbr(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 5))}
                                    placeholder="e.g., BBTM"
                                    maxLength={5}
                                    className="BoardList__create-abbr-input"
                                />
                            </div>
                            <div className="BoardList__create-field">
                                <label>Description (optional)</label>
                                <textarea
                                    value={newBoardDesc}
                                    onChange={(e) => setNewBoardDesc(e.target.value)}
                                    placeholder="What's this board for?"
                                    rows={3}
                                />
                            </div>
                            <div className="BoardList__create-actions">
                                <button
                                    type="button"
                                    className="BoardList__create-cancel"
                                    onClick={() => setShowCreate(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="BoardList__create-submit"
                                    disabled={creating || !newBoardName.trim()}
                                >
                                    {creating ? 'Creating...' : 'Create Board'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {loading ? (
                    <div className="BoardList__loading">
                        <div className="LoadingScreen__spinner" />
                    </div>
                ) : boards.length === 0 ? (
                    <div className="BoardList__empty">
                        <div className="BoardList__empty-icon">📋</div>
                        <h3>No boards yet</h3>
                        <p>Create your first board to start organizing tasks</p>
                        <button
                            className="BoardList__create-btn"
                            onClick={() => setShowCreate(true)}
                        >
                            + Create Board
                        </button>
                    </div>
                ) : (
                    <div className="BoardList__grid">
                        {boards.map(board => (
                            <div
                                key={board.id}
                                className="BoardList__card"
                                onClick={() => navigate(`/board/${board.id}`)}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    showContextMenu(e.clientX, e.clientY, board);
                                }}
                            >
                            <div className="BoardList__card-header">
                                    <div className="BoardList__card-title-row">
                                        {board.abbreviation && (
                                            <span className="BoardList__card-abbr">{board.abbreviation}</span>
                                        )}
                                        <h3 className="BoardList__card-name">{board.name}</h3>
                                        {board.user_role && board.user_role !== 'OWNER' && (
                                            <span className={`BoardList__card-role BoardList__card-role--${board.user_role}`}>
                                                {board.user_role}
                                            </span>
                                        )}
                                        {board.is_public && (
                                            <span className="BoardList__card-public">
                                                <i className="fas fa-globe" /> Public
                                            </span>
                                        )}
                                        {board.linked_workspace && (
                                            <span className="BoardList__card-linked-workspace" title={`Linked to ${board.linked_workspace.workspace_name}`}>
                                                <i className="fas fa-link" /> {board.linked_workspace.workspace_name}
                                            </span>
                                        )}
                                    </div>
                                    {board.user_role === 'OWNER' && (
                                        <button
                                            className="BoardList__card-delete"
                                            onClick={(e) => handleDeleteBoard(board.id, e)}
                                            title="Delete board"
                                            disabled={deletingBoardId === board.id}
                                        >
                                            {deletingBoardId === board.id
                                                ? <span className="btn-spinner btn-spinner--danger" />
                                                : '×'}
                                        </button>
                                    )}
                                </div>
                                {board.description && (
                                    <p className="BoardList__card-desc">{board.description}</p>
                                )}
                                <div className="BoardList__card-footer">
                                    <span className="BoardList__card-tasks">
                                        {board.task_count} {board.task_count === 1 ? 'task' : 'tasks'}
                                        {board.member_count > 0 && (
                                            <> · <i className="fas fa-users" style={{ fontSize: '10px', opacity: 0.6 }} /> {board.member_count + 1}</>    
                                        )}
                                    </span>
                                    <span className="BoardList__card-date">
                                        {formatDate(board.updated_at)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {ContextMenuComponent}
        </div>
    );
};

export default BoardList;
